import {BehaviorSubject, of, Subject, Subscription} from 'rxjs';
import {v4 as uuid} from 'uuid';

import {
  type Analytics,
  type Client,
  type ClientEvent,
  type ClientResponse,
  type ClientStatus,
  type Conversation,
  type ConversationActionParams,
  type ConversationChannel,
  type ConversationEvent,
  type ConversationParticipant,
  ConversationStatus,
  type Errors,
  type GetConversationMessagesParams,
  type GetConversationParticipantsParams,
  type LeaveConversationParams,
  type Metrics,
  type RequestAction,
  type SendConversationMessageParams,
  type SendConversationTypingParams,
  type SendFeedbackParams,
  type StreamTextParams,
} from '@stripe-internal/conversations-client';
import type {ConversationsApiClient} from '@stripe-internal/conversations-api-client';
import type {InlineDataEventBus} from '@stripe-internal/inline-data';

import type {Api} from '../../core/lib/api/api';
import type {AppFeatureFlags} from '../../core/types';
import {METRIC} from '../../core/lib/util/clientMetrics';
import {syncStorageModeForThreadsChannel} from './storage';
import {ThreadsMessageHandler} from './ThreadsMessageHandler';

// Messaging transports only; RAC and email are conversation workflow channels.
export type ClientTransportChannel = Extract<
  ConversationChannel,
  'bot' | 'chat'
>;

type BoundChannel =
  | {channel: 'bot'; chatId?: never}
  | {channel: 'chat'; chatId: string};

/** Binding an existing chat and creating a new chat are explicit choices. */
export type TargetChannel =
  | (BoundChannel & {create?: never})
  | {channel: 'chat'; create: true; chatId?: never};

export type ChannelState =
  | (BoundChannel & {state: 'bound'; error?: never})
  | {channel: 'chat'; chatId?: string; state: 'switching'; error?: never}
  | {channel: 'bot'; state: 'failed'; error: Error};

export type ChannelResult = 'bound' | 'aborted' | 'failed';

/**
 * Lifecycle moments the client observes that the conversation log needs to
 * reflect. The client emits them; presenting them is the controller's job.
 */
export type ConversationLifecycleEvent = {
  name: 'chat-starting' | 'chat-ended';
};

export type ThreadsConversationIdentity = {
  conversationId: string;
  chatId?: string;
};

export type ThreadsBotTransport = Pick<
  ConversationsApiClient,
  | Extract<keyof Client, keyof ConversationsApiClient>
  | 'waitForPendingMessagesHandled'
  | 'busyObservable'
  | 'isPending'
>;

export type ThreadsConversationClientOptions = {
  transports: {
    uhClient: ThreadsBotTransport;
    chatClient: Client;
  };
  api: Pick<Api, 'abandonChat'>;
  getFeatureFlags: () => AppFeatureFlags;
  requestAction: (action: RequestAction) => Promise<ClientResponse>;
  inlineDataEventBus: InlineDataEventBus;
  withHistory: boolean;
  endChatConversationOnLeave: boolean;
};

type ChannelRoute = {
  channel: ClientTransportChannel;
  client: Client;
  conversationId: string;
};

type PendingTransition = {
  chatId?: string;
  abort: AbortController;
  result: Promise<ChannelResult>;
};

const unsupportedOperation = async () => ({
  code: 400,
  error: 'Operation unavailable on a bound messaging router',
});

/**
 * A per-conversation Client that routes one canonical (bot) conversation
 * through the shared bot and chat transports, relabeling inbound events to the
 * canonical ID. It implements Client directly rather than extending
 * AbstractClient: the transports already own window listeners, observability,
 * and participant caches, and a per-conversation instance must not duplicate
 * them.
 */
export class ThreadsConversationClient implements Client {
  readonly conversationId: string;

  private readonly clientType = 'threads_conversation_client';

  /** Chat whose transport events we observe, even before or after routing to it. */
  private associatedChatId?: string;

  private activeRoute: BoundChannel = {channel: 'bot'};

  private disposed = false;

  private subscriptions = new Subscription();

  private statusSubscription?: Subscription;

  private status$: BehaviorSubject<ClientStatus>;

  private events$ = new Subject<ClientEvent>();

  private conversationEvents$ = new Subject<ConversationEvent>();

  private chatTransportEvents$ = new Subject<ConversationEvent>();

  private lifecycle$ = new Subject<ConversationLifecycleEvent>();

  private listeners = new Map<(event: ClientEvent) => void, Subscription>();

  private conversationListeners = new Map<
    (event: ConversationEvent) => void,
    Subscription
  >();

  private model?: Conversation;

  private messageHandler?: ThreadsMessageHandler;

  private modelSubscription?: Subscription;

  private pollingEnabled = true;

  private pendingTransition?: PendingTransition;

  private transitionVersion = 0;

  private channelState$ = new BehaviorSubject<ChannelState>({
    channel: 'bot',
    state: 'bound',
  });

  private endedEmitted = false;

  private leaving = 0;

  private transportStatuses = new Map<string, ConversationStatus>();

  private greetingShown = false;

  constructor(
    identity: ThreadsConversationIdentity,
    private options: ThreadsConversationClientOptions,
  ) {
    if (!identity.conversationId) {
      throw new Error('Explicit conversation ID required');
    }
    this.conversationId = identity.conversationId;
    this.associatedChatId = identity.chatId;
    this.status$ = new BehaviorSubject(this.transport.getStatus());
    for (const [channel, transport] of [
      ['bot', options.transports.uhClient],
      ['chat', options.transports.chatClient],
    ] as const) {
      this.subscriptions.add(
        transport
          .conversationEventsObservable()
          .subscribe((event) => this.handleTransportEvent(channel, event)),
      );
      this.subscriptions.add(
        transport.eventsObservable().subscribe((event) => {
          if (
            !this.disposed &&
            this.activeRoute.channel === channel &&
            event.name === 'client-exception'
          ) {
            this.events$.next(event);
          }
        }),
      );
    }
    this.mirrorStatus();
  }

  private handleTransportEvent(
    channel: ClientTransportChannel,
    event: ConversationEvent,
  ): void {
    if (this.disposed) {
      return;
    }
    const owned =
      channel === 'bot'
        ? event.conversationId === this.conversationId
        : event.conversationId === this.associatedChatId ||
          event.conversationId === this.pendingTransition?.chatId;
    if (!owned) {
      return;
    }
    if (event.name === 'conversation-updated') {
      this.transportStatuses.set(event.conversationId, event.status);
    }
    if (channel === 'chat') {
      this.chatTransportEvents$.next(event);
    }
    const routed =
      this.activeRoute.channel === channel &&
      (this.activeRoute.channel === 'bot' ||
        event.conversationId === this.activeRoute.chatId);
    if (routed) {
      this.conversationEvents$.next({
        ...event,
        conversationId: this.conversationId,
      });
    }
  }

  private get transport(): Client {
    return this.activeRoute.channel === 'chat'
      ? this.options.transports.chatClient
      : this.options.transports.uhClient;
  }

  get channel(): ConversationChannel {
    return this.activeRoute.channel;
  }

  /** The chat currently routed to, if any. */
  get chatConversationId(): string | undefined {
    return this.activeRoute.channel === 'chat'
      ? this.activeRoute.chatId
      : undefined;
  }

  get uhConversationId(): string {
    return this.conversationId;
  }

  get type(): string {
    return this.clientType;
  }

  get syncStorageMode() {
    return syncStorageModeForThreadsChannel(
      this.activeRoute.channel,
      this.transport.syncStorageMode,
      this.options.withHistory,
    );
  }

  get featureFlags(): AppFeatureFlags {
    return this.options.getFeatureFlags();
  }

  get analytics(): Analytics {
    return this.options.transports.uhClient.analytics;
  }

  get errors(): Errors {
    return this.options.transports.uhClient.errors;
  }

  get metrics(): Metrics {
    return this.options.transports.uhClient.metrics;
  }

  get channelState(): ChannelState {
    return this.channelState$.getValue();
  }

  channelStateObservable() {
    return this.channelState$.asObservable();
  }

  getStatus(): ClientStatus {
    return this.status$.getValue();
  }

  statusObservable() {
    return this.status$.asObservable();
  }

  eventsObservable() {
    return this.events$.asObservable();
  }

  conversationEventsObservable() {
    return this.conversationEvents$.asObservable();
  }

  /**
   * Raw chat-transport events for this conversation's chat, including while a
   * transition is still initializing or while routed to the bot.
   */
  chatTransportEventsObservable() {
    return this.chatTransportEvents$.asObservable();
  }

  lifecycleObservable() {
    return this.lifecycle$.asObservable();
  }

  setPollingEnabled(enabled: boolean): void {
    this.pollingEnabled = enabled;
  }

  private mirrorStatus(): void {
    if (this.disposed) {
      return;
    }
    this.statusSubscription?.unsubscribe();
    const emit = (status: ClientStatus) => {
      this.status$.next(status);
      this.events$.next({
        id: uuid(),
        timestamp: Date.now(),
        name: 'client-status',
        status,
      });
    };
    emit(this.transport.getStatus());
    this.statusSubscription = this.transport.statusObservable().subscribe(emit);
  }

  attachModel(model: Conversation): void {
    if (this.disposed) {
      throw new Error('Conversation client disposed');
    }
    if (model.id !== this.conversationId) {
      throw new Error('Conversation model identity mismatch');
    }
    if (this.model === model) {
      return;
    }
    if (this.model) {
      throw new Error('Conversation model already attached');
    }
    this.model = model;
    this.messageHandler = new ThreadsMessageHandler({
      conversation: model,
      bot: this.options.transports.uhClient,
      getFeatureFlags: this.options.getFeatureFlags,
      isChat: () => this.activeRoute.channel === 'chat',
      inlineDataEventBus: this.options.inlineDataEventBus,
      requestAction: this.options.requestAction,
    });
    this.modelSubscription = model.eventsObservable().subscribe((event) => {
      if (this.disposed) {
        return;
      }
      if (event.name === 'conversation-ended') {
        this.emitEnded();
      }
      if (event.name === 'conversation-request-action') {
        const {respondWith} = event;
        this.options
          .requestAction({
            name: 'request-action',
            action: event.action,
            waitForResponse: !!respondWith,
            responseTimeout: event.responseTimeout,
          })
          .then((response) => {
            if (!this.disposed) {
              respondWith?.(
                response.data as Record<string, unknown> | undefined,
              );
            }
          })
          .catch((error) => this.report(String(error)));
      }
    });
  }

  private get routedConversationId(): string {
    return this.activeRoute.channel === 'chat'
      ? this.activeRoute.chatId
      : this.conversationId;
  }

  /**
   * Emits chat-ended once per chat session. While a leave is in flight the
   * model is already marked inactive, so the end is only emitted once the
   * transport has confirmed it or the leave has settled.
   */
  private emitEnded(): void {
    if (this.disposed || !this.model?.hasEnded || this.endedEmitted) {
      return;
    }
    const transportEnded =
      this.transportStatuses.get(this.routedConversationId) ===
      ConversationStatus.Inactive;
    if (this.leaving > 0 && !transportEnded) {
      return;
    }
    this.endedEmitted = true;
    this.lifecycle$.next({name: 'chat-ended'});
  }

  /**
   * Switches routing only; it does not initialize the chat transport. switchToChat
   * calls this once the transport is ready.
   */
  private setRoute(route: BoundChannel): void {
    const {channel, chatId} = route;
    if (this.disposed) {
      return;
    }
    if (channel === 'chat') {
      // A newly routed chat session gets its own ended notice.
      if (
        this.activeRoute.channel !== 'chat' ||
        this.activeRoute.chatId !== chatId
      ) {
        this.endedEmitted = false;
      }
      this.associatedChatId = chatId;
    }
    this.activeRoute = route;
    this.mirrorStatus();
  }

  cancelChannelTransition(): void {
    this.transitionVersion += 1;
    this.pendingTransition?.abort.abort();
    if (this.pendingTransition) {
      this.setRoute({channel: 'bot'});
      this.channelState$.next({channel: 'bot', state: 'bound'});
    }
  }

  async ensureChannel(target: TargetChannel): Promise<ChannelResult> {
    if (this.disposed) {
      return 'aborted';
    }
    const version = this.transitionVersion;
    if (this.pendingTransition) {
      const pendingId = this.pendingTransition.chatId;
      const result = await this.pendingTransition.result;
      if (this.disposed || version !== this.transitionVersion) {
        return 'aborted';
      }
      if (
        target.channel === 'chat' &&
        pendingId === target.chatId &&
        result !== 'aborted'
      ) {
        return result;
      }
      return this.ensureChannel(target);
    }
    if (target.channel === 'bot') {
      if (
        this.activeRoute.channel === 'bot' &&
        this.channelState.state === 'bound'
      ) {
        return 'bound';
      }
      this.setRoute({channel: 'bot'});
      this.channelState$.next({channel: 'bot', state: 'bound'});
      return 'bound';
    }
    if (
      this.activeRoute.channel === 'chat' &&
      (!target.chatId || this.activeRoute.chatId === target.chatId)
    ) {
      return 'bound';
    }
    if (this.activeRoute.channel === 'bot') {
      this.associatedChatId = target.chatId;
    }
    const abort = new AbortController();
    const result = Promise.resolve().then(() =>
      this.switchToChat(target, abort.signal),
    );
    const transition: PendingTransition = {
      chatId: target.chatId,
      abort,
      result,
    };
    this.pendingTransition = transition;
    this.channelState$.next({
      channel: 'chat',
      chatId: target.chatId,
      state: 'switching',
    });
    const settled = result.finally(() => {
      if (this.pendingTransition === transition) {
        this.pendingTransition = undefined;
      }
    });
    let cancel!: () => void;
    const interrupted = new Promise<ChannelResult>((resolve) => {
      cancel = () => resolve('aborted');
      abort.signal.addEventListener('abort', cancel, {once: true});
    });
    try {
      return await Promise.race([settled, interrupted]);
    } finally {
      abort.signal.removeEventListener('abort', cancel);
    }
  }

  private async switchToChat(
    target: Extract<TargetChannel, {channel: 'chat'}>,
    signal: AbortSignal,
  ): Promise<ChannelResult> {
    const declaredChatId = target.chatId;
    const conversation = this.model;
    let bound = false;
    try {
      if (signal.aborted) {
        return 'aborted';
      }
      if (!conversation) {
        throw new Error('Conversation not attached');
      }
      if (!declaredChatId && !target.create) {
        throw new Error('Explicit chat ID or creation intent required');
      }
      if (target.create) {
        this.errors.reportWarning('Creating chat without server chat id');
      }
      if (this.activeRoute.channel !== 'chat') {
        this.lifecycle$.next({name: 'chat-starting'});
      }
      await conversation.disconnect();
      if (signal.aborted) {
        return 'aborted';
      }
      // Without a declared ID the chat transport creates the chat itself via
      // /api/start-chat, using the conversation context for the request body.
      const response =
        await this.options.transports.chatClient.startConversation({
          conversationId: declaredChatId,
          participant: conversation.user,
          context: conversation.context,
          uhConversationId: this.conversationId,
          connect: false,
          fetch: false,
          syncStorage: false,
        });
      if (signal.aborted) {
        return 'aborted';
      }
      if ('error' in response) {
        throw new Error(response.error);
      }
      const chatId = declaredChatId ?? response.data.conversation.id;
      if (!chatId) {
        throw new Error('Chat ID missing from acquisition response');
      }
      this.setRoute({channel: 'chat', chatId});
      const context = {
        bot_conversationId: this.conversationId,
        chat_conversationId: chatId,
        channel: 'chat' as const,
        requestedChannel: 'chat',
        intent: 'contact_support',
      };
      const changed = Object.fromEntries(
        Object.entries(context).filter(
          ([key, value]) => conversation.context[key] !== value,
        ),
      );
      if (Object.keys(changed).length) {
        conversation.setContextValues(changed);
      }
      if (signal.aborted) {
        return 'aborted';
      }
      // Twilio initialization does not report the active status declared by the server.
      if (conversation.status !== ConversationStatus.Active) {
        await conversation.handleEventFromClient({
          id: uuid(),
          timestamp: Date.now(),
          name: 'conversation-updated',
          conversationId: this.conversationId,
          status: ConversationStatus.Active,
        });
      }
      if (signal.aborted) {
        return 'aborted';
      }
      await conversation.connect({fetch: true});
      if (signal.aborted) {
        return 'aborted';
      }
      if (conversation.context.enabledInputModes !== undefined) {
        conversation.setContextValue('enabledInputModes', undefined);
      }
      this.track(METRIC.transition_to_chat_success);
      bound = true;
      this.channelState$.next({channel: 'chat', chatId, state: 'bound'});
      return 'bound';
    } catch (cause) {
      if (signal.aborted) {
        return 'aborted';
      }
      const error = cause instanceof Error ? cause : new Error(String(cause));
      this.report(`exception switching to chat: ${error.message}`);
      this.track(METRIC.transition_to_chat_error);
      this.channelState$.next({channel: 'bot', state: 'failed', error});
      return 'failed';
    } finally {
      if (!bound) {
        this.setRoute({channel: 'bot'});
        await conversation?.disconnect();
      }
    }
  }

  private assertOwns(conversationId: string): void {
    if (this.disposed || conversationId !== this.conversationId) {
      throw new Error('Conversation runtime unavailable');
    }
  }

  private get currentChannelRoute(): ChannelRoute {
    return {
      channel: this.activeRoute.channel,
      client: this.transport,
      conversationId: this.routedConversationId,
    };
  }

  async action(params: ConversationActionParams): Promise<ClientResponse> {
    try {
      this.assertOwns(params.conversationId);
      const route = this.currentChannelRoute;
      if (!window.navigator.onLine) {
        return {code: 408, error: 'No network connection'};
      }
      if (route.channel === 'chat') {
        const botOnly = ['handle-trigger-params', 'inline-data-result'];
        if (botOnly.includes(String(params.action.name))) {
          return {code: 205};
        }
        return route.client.action({
          ...params,
          conversationId: route.conversationId,
        });
      }
      if (!this.messageHandler) {
        throw new Error('Conversation not attached');
      }
      return await this.messageHandler.handleAction(params, (request) =>
        route.client.action({...request, conversationId: route.conversationId}),
      );
    } catch (error) {
      return this.failure(error);
    }
  }

  async sendConversationMessage(
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> {
    try {
      this.assertOwns(params.conversationId);
      const route = this.currentChannelRoute;
      if (!this.messageHandler) {
        throw new Error('Conversation not attached');
      }
      return await this.messageHandler.sendMessage(params, (request) =>
        route.client.sendConversationMessage({
          ...request,
          conversationId: route.conversationId,
        }),
      );
    } catch (error) {
      return this.failure(error);
    }
  }

  getConversationMessages(params: GetConversationMessagesParams) {
    this.assertOwns(params.conversationId);
    const route = this.currentChannelRoute;
    return route.client.getConversationMessages({
      ...params,
      conversationId: route.conversationId,
    });
  }

  getConversationParticipants(params: GetConversationParticipantsParams) {
    this.assertOwns(params.conversationId);
    const route = this.currentChannelRoute;
    if (route.channel === 'chat') {
      return route.client.getConversationParticipants({
        ...params,
        conversationId: route.conversationId,
      });
    }
    return this.options.transports.uhClient.getConversationParticipants();
  }

  sendConversationTyping(params: SendConversationTypingParams) {
    this.assertOwns(params.conversationId);
    const route = this.currentChannelRoute;
    if (route.channel === 'chat') {
      return route.client.sendConversationTyping({
        ...params,
        conversationId: route.conversationId,
      });
    }
    return this.options.transports.uhClient.sendConversationTyping();
  }

  sendFeedback(params: SendFeedbackParams) {
    this.assertOwns(params.conversationId);
    return this.options.transports.uhClient.sendFeedback({
      ...params,
      conversationId: this.conversationId,
    });
  }

  streamText(params: StreamTextParams) {
    this.assertOwns(params.conversationId);
    const route = this.currentChannelRoute;
    return route.client.streamText({
      ...params,
      conversationId: route.conversationId,
    });
  }

  pollConversation(params: {
    conversationId: string;
    participantsById: Record<string, ConversationParticipant>;
  }) {
    this.assertOwns(params.conversationId);
    const bot = this.options.transports.uhClient;
    if (
      !this.pollingEnabled ||
      this.activeRoute.channel !== 'bot' ||
      !('pollConversation' in bot) ||
      typeof bot.pollConversation !== 'function'
    ) {
      return Promise.resolve({type: 'success' as const, messages: {}});
    }
    return bot.pollConversation({
      ...params,
      conversationId: this.conversationId,
    });
  }

  async leaveConversation(
    params: LeaveConversationParams,
  ): Promise<ClientResponse> {
    this.assertOwns(params.conversationId);
    const route = this.currentChannelRoute;
    let response: ClientResponse;
    this.leaving += 1;
    try {
      response = await this.leaveViaRoute(params, route);
    } catch (error) {
      response = this.failure(error);
    }
    try {
      const stillRouted =
        this.activeRoute.channel === route.channel &&
        this.routedConversationId === route.conversationId;
      if (
        response.code >= 400 &&
        !this.disposed &&
        this.model &&
        stillRouted &&
        this.transportStatuses.get(route.conversationId) !==
          ConversationStatus.Inactive
      ) {
        // Conversation.leave marks the model inactive before its request
        // succeeds. Transports report status only on changes, so the absence of
        // a transport-reported end is the condition for restoring it; a
        // reported end must never be undone here.
        await this.model.handleEventFromClient({
          id: uuid(),
          timestamp: Date.now(),
          name: 'conversation-updated',
          conversationId: this.conversationId,
          status: ConversationStatus.Active,
        });
        this.endedEmitted = false;
      }
    } finally {
      this.leaving -= 1;
      this.emitEnded();
    }
    return response;
  }

  private async leaveViaRoute(
    params: LeaveConversationParams,
    route: ChannelRoute,
  ): Promise<ClientResponse> {
    const conversation = this.model;
    const agentJoined =
      conversation &&
      Object.values(conversation.participants).some(
        (participant) => participant.role === 'agent',
      );
    if (route.channel === 'chat' && conversation && !agentJoined) {
      this.options.api
        .abandonChat({conversationId: route.conversationId})
        .then(() => this.track(METRIC.abandon_chat_success))
        .catch((error: Error) => {
          this.report(`exception handling abandoned chat: ${error.message}`);
          this.track(METRIC.abandon_chat_error);
        });
    }
    const response = await route.client.leaveConversation({
      ...params,
      conversationId: route.conversationId,
    });
    // The SDK preserves the builder's /end versus delete endpoint choice.
    if (route.channel === 'chat' && this.options.endChatConversationOnLeave) {
      return this.options.transports.uhClient.leaveConversation({
        ...params,
        conversationId: this.conversationId,
      });
    }
    return response;
  }

  startConversation = unsupportedOperation;

  inProgress = unsupportedOperation;

  requestACall = unsupportedOperation;

  getVerificationCodes = unsupportedOperation;

  submitEmailIssue = unsupportedOperation;

  async disconnect(): Promise<void> {
    await this.model?.disconnect();
  }

  isBusy(): boolean {
    return this.messageHandler?.isBusy() ?? false;
  }

  busyObservable() {
    return this.messageHandler?.busyObservable() ?? of(false);
  }

  hasShownInitialGreeting(): boolean {
    return this.greetingShown;
  }

  markInitialGreetingAsShown(): void {
    this.greetingShown = true;
  }

  track = (name: string, params: Record<string, string | number> = {}) => {
    const tags = {client_type: 'support_conversations_client', ...params};
    this.metrics.increment(name, tags);
    this.analytics.track(name, tags);
  };

  report = (message: string) => {
    const sentryEventId = this.errors.reportError(message, {
      tags: {
        client_type: 'support_conversations_client',
        assistant_error_screen: true,
      },
    });
    if (!this.disposed) {
      this.events$.next({
        id: uuid(),
        timestamp: Date.now(),
        name: 'client-exception',
        error: message,
        sentryEventId,
      });
    }
    return sentryEventId;
  };

  private failure(error: unknown): ClientResponse {
    const message = error instanceof Error ? error.message : String(error);
    this.report(message);
    return {code: 500, error: message};
  }

  addListener(listener: (event: ClientEvent) => void): void {
    if (!this.disposed && !this.listeners.has(listener)) {
      this.listeners.set(listener, this.events$.subscribe(listener));
    }
  }

  removeListener(listener: (event: ClientEvent) => void): void {
    this.listeners.get(listener)?.unsubscribe();
    this.listeners.delete(listener);
  }

  addConversationListener(
    id: string,
    listener: (event: ConversationEvent) => void,
  ): void {
    if (this.disposed || id !== this.conversationId) {
      return;
    }
    if (!this.conversationListeners.has(listener)) {
      this.conversationListeners.set(
        listener,
        this.conversationEvents$.subscribe(listener),
      );
    }
  }

  removeConversationListener(
    id: string,
    listener: (event: ConversationEvent) => void,
  ): void {
    if (id !== this.conversationId) {
      return;
    }
    this.conversationListeners.get(listener)?.unsubscribe();
    this.conversationListeners.delete(listener);
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.cancelChannelTransition();
    this.messageHandler?.dispose();
    this.subscriptions.unsubscribe();
    this.statusSubscription?.unsubscribe();
    this.modelSubscription?.unsubscribe();
    this.listeners.forEach((sub) => sub.unsubscribe());
    this.listeners.clear();
    this.conversationListeners.forEach((sub) => sub.unsubscribe());
    this.conversationListeners.clear();
    this.model = undefined;
    this.messageHandler = undefined;
    this.status$.complete();
    this.events$.complete();
    this.conversationEvents$.complete();
    this.chatTransportEvents$.complete();
    this.lifecycle$.complete();
    this.channelState$.complete();
  }
}
